import React from 'react';
import { Alert, AlertTitle, Box, Snackbar, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  BatteryLandingAlert,
  batteryLandingAlertDismissed,
} from '../../store/slices/safetySlice';
import { formatLatLng } from '../../utils/formatters';

/**
 * A drone is putting itself down on a low battery (#629).
 *
 * It arrives before touchdown, over the same WebSocket the boundary alerts use,
 * and it does not time out. An aircraft that landed itself in a field while
 * nobody happened to be looking at the right moment is precisely what must not
 * happen quietly — so these stay until an operator dismisses them.
 *
 * The position is the message. "DRONE-1 is landing itself" tells an operator
 * something happened; "at 52.2297, 21.0122 on 9 % battery" tells them where to
 * go. A drone that put itself down somewhere its pilot did not choose has to be
 * retrieved, and the coordinates are the whole reason this event exists rather
 * than a state change nobody narrates.
 */

/**
 * Where the drone is coming down, as far as it knows (#751, B23). Without a GPS
 * fix it does not know, and says so rather than showing a place nobody
 * measured.
 */
const describePlace = (alert: BatteryLandingAlert): string => {
  if (alert.latitude === undefined || alert.longitude === undefined) {
    return 'position unknown (no GPS fix)';
  }
  const at = formatLatLng(alert.latitude, alert.longitude);
  return alert.positionIsLastFixed ? `near ${at} (last GPS fix)` : `at ${at}`;
};

/** What happened, and what the operator is expected to do about it. */
const describeLanding = (alert: BatteryLandingAlert): string => {
  // No charge when the autopilot measures none: the voltage below decided (#751).
  const charge =
    alert.batteryPercent !== undefined ? ` on ${Math.round(alert.batteryPercent)}% battery` : '';
  const where = `${describePlace(alert)}${charge}`;
  const volts =
    alert.voltsPerCell !== undefined
      ? ` (${alert.voltsPerCell.toFixed(2)} V per cell${
          alert.thresholdVoltsPerCell !== undefined
            ? `, threshold ${alert.thresholdVoltsPerCell.toFixed(2)}`
            : ''
        })`
      : '';

  // The two cases differ in what the operator should do, not merely in detail.
  // When a remote pilot has the aircraft, this software has stood down (#631) and
  // nothing is bringing the drone down but that pilot and the autopilot's own
  // failsafe — so the useful action is to reach the pilot, not to watch a descent.
  const who = alert.remotePilotInCommand
    ? 'A remote pilot has the aircraft, so the onboard automation has stood down and did NOT command this landing — reach the pilot.'
    : 'The onboard automation commanded the landing.';

  return `Descending ${where}${volts}. ${who}`;
};

export const BatteryLandingAlerts: React.FC = () => {
  const dispatch = useDispatch();
  const alerts = useSelector((state: RootState) => state.safety.batteryLandingAlerts);
  const drones = useSelector((state: RootState) => state.drones.drones);

  if (alerts.length === 0) return null;

  // Fall back to the ID: an alert about a drone the UI has not loaded yet is
  // still an alert worth showing.
  const nameFor = (droneId: string): string => drones[droneId]?.name ?? droneId;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ maxWidth: { xs: '100%', sm: 420 } }}
    >
      <Box
        role="region"
        aria-label="Automatic landing alerts"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
      >
        {alerts.map((alert) => (
          <Alert
            key={alert.id}
            // An aircraft coming down on a dead pack with nobody commanding it is
            // not the same event as one our own automation is landing in an
            // orderly way, and they must not look the same.
            severity={alert.remotePilotInCommand ? 'error' : 'warning'}
            role="alert"
            onClose={() => dispatch(batteryLandingAlertDismissed(alert.id))}
            variant="filled"
          >
            <AlertTitle>{nameFor(alert.droneId)} is landing itself on a low battery</AlertTitle>
            <Typography variant="body2">{describeLanding(alert)}</Typography>
          </Alert>
        ))}
      </Box>
    </Snackbar>
  );
};
